import type { CodesysCompileLog } from '@realplc/types';
import type { Message } from '../../types';
import { deriveDisplayMode } from '../displayStrategy';
import {
  getEffectiveValidationReport,
  getRepairBlockedContext,
  getRepairDisplayStatus,
  isRepairStageDone,
} from '../repairDisplayState';
import { getValidationRootCauseSummary } from '../validationRootCauseSummary';
import { decisionCountsFromClosure } from '../specClosureCounts';
import { readDeliveryReview } from '../deliveryValidationLabel';
import { findDeliveredVersion, getRepairCodeVersions, serverDeclaredNoProgram } from '../messageTopic';
import { measureCodeChange } from '../codeChange';
import { buildStageTimelineItems } from '../stageTimelineAdapter';
import {
  getWorkspaceMessageCode,
  getWorkspaceMessagePlanReview,
  getWorkspaceValidationReport,
} from '../workspaceViewModel';
import { getWorkflowStageGates } from '../workflowStageGuards';
import {
  hideIncompleteStreamingMermaid,
  normalizeStructuredMarkers,
  stripStructuredTags,
} from '../structuredResponse';
import { getFailureReason } from './failureReason';
import { deriveAttention } from './attentionPolicy';
import { buildArtifacts, resolveDeliverableName } from './artifactPolicy';
import { getActiveAuditItem, getStatusText } from './statusTextAdapter';
import { buildTurnSteps, splitTurnOpening } from './turnSteps';
import { resolveFinalElapsedMs } from './turnElapsed';
import type {
  ArtifactFile,
  BuildTurnPresentationOptions,
  TurnAction,
  NativeCompileRecord,
  TurnKind,
  TurnPresentation,
  TurnRepairAttempt,
  TurnValidationIssue,
} from './presentationTypes';

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function translate(options: BuildTurnPresentationOptions, key: string, fallback: string): string {
  const value = options.translate?.(key);
  return value && value !== key ? value : fallback;
}

/**
 * One-line derivative of a text: used for status lines, previews and filenames.
 *
 * This is deliberately lossy and must never touch a turn's body text — see
 * `stripInternal`, which is the body's only filter.
 */
function compactText(value: unknown, max = 280): string {
  if (!hasText(value)) return '';
  const cleaned = value
    .replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '')
    .replace(/```mermaid[\s\S]*?```/gi, '')
    .replace(/```(?:json)?[\s\S]*?```/gi, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max).trimEnd()}…`;
}

/**
 * A `[code_hash: …]` line the model wrote under its program.
 *
 * 2026-09-23 线上（Siemens 回合）：「生成代码」里程序的代码块之后单独一行
 * `[code_hash: 8f60…]`。No code writes or reads such a line: the model wrote it
 * after its own fence, and the body printed what follows a fence as it stands.
 * Whole lines only, and never inside a fence — there it is part of the program
 * the compiler is given, and the page must not show a program other than that one.
 */
const STRAY_HASH_LINE = /^\s*\[(?:code|source)_hash:\s*[0-9a-f]{16,}\]\s*$/gim;
/** A fenced block, or one still open at the end of the text, as one split part. */
const FENCED_BLOCK = /(```[\s\S]*?(?:```|$))/;

/**
 * Body filter: removes internal scaffolding only.
 *
 * Headings, lists, tables, fenced code and mermaid all survive so the renderer
 * — not the data layer — decides how much of a long answer is shown at once.
 */
function stripInternal(value: unknown): string {
  if (!hasText(value)) return '';
  return stripStructuredTags(
    value
      .replace(/<think>[\s\S]*?(?:<\/think>|$)/gi, '')
      // The opening line and the step conclusion are rendered by TurnStream from
      // their own channels. If a marker survives the runtime splitter it must
      // still never reach the body.
      .replace(/\[START_STEP_CONCLUSION\][\s\S]*?(?:\[END_STEP_CONCLUSION\]|$)/g, '')
      .replace(/\[START_ECHO\][\s\S]*?(?:\[END_ECHO\]|$)/g, '')
      // 全局信号清单是给模型的规划脚手架与后处理的核对锚点，不是交付内容：
      // 它能从代码的 VAR_GLOBAL 块完整还原。边界用前瞻而非 `$`——本小节紧邻
      // [START_CODE]，若模型漏写 [END_GLOBALS]，`$` 形式会把整个代码段连同
      // 其后所有小节一并吞掉。前瞻还必须认代码围栏（```）本身：本函数在
      // `fenceProgramSections` 之后运行，届时 [START_CODE] 标记已被替换成裸
      // 的 ```st 围栏，不再是可匹配的 [START_x] 标记——缺了这条，未闭合的
      // GLOBALS 依然会一路吞到围栏内的代码正文。
      .replace(/\[START_GLOBALS\][\s\S]*?(?=\[END_GLOBALS\]|\[\/?START_[A-Z0-9_]+\]|`{3,}|$)/gi, '')
      // The follow-up suggestions have their own render — the chips under the
      // turn — and the prompt asks for them "one per line, no numbering or
      // bullets". A copy left here is therefore a run of bare lines, which
      // markdown fuses into a single paragraph: three unrelated next steps
      // arrive as one garbled sentence, and it lands directly above the
      // program. Bounded by lookahead like GLOBALS above: this is the last
      // section, so a missing [END_FOLLOWUP] is routine, and recognising a
      // fence keeps an unclosed one from swallowing a code block the model
      // went on to write after it.
      .replace(/\[START_FOLLOWUP\][\s\S]*?(?=\[END_FOLLOWUP\]|\[\/?START_[A-Z0-9_]+\]|`{3,}|$)/gi, '')
      // 模型自创的澄清块。2026-09-15 线上：用户答完 12 张卡之后，代码生成阶段
      // 又冒出一个 [CLARIFY]，21 行 A./B./C. 选项连同 <!-- --> 注释原样渲染，
      // 而那些问题从未变成卡片。
      //
      // 两道提示词指令都没拦住：生成段写着「你在这一阶段没有任何提问渠道」，
      // [START_QUESTIONS] 那节还专门写了「这不是澄清卡」。再加一句提示词只是
      // 加 token。防线只能在这里——下面那条通用擦除只认 [START_x]/[END_x]，
      // 裸标记连同它整段内容一起漏过去。
      //
      // 边界用前瞻，理由和上面的 GLOBALS / FOLLOWUP 一样：模型漏写闭合标记是
      // 常态，用 `$` 收尾会把其后的程序一起吞掉。
      .replace(/\[CLARIFY\][\s\S]*?(?=\[\/CLARIFY\]|\[\/?(?:START|END)_[A-Z0-9_]+\]|`{3,}|$)/gi, '')
      .replace(/\[\/?CLARIFY\]/gi, '')
      .replace(/\[END_STEP_CONCLUSION\]|\[END_ECHO\]|\[END_GLOBALS\]|\[END_FOLLOWUP\]/g, '')
      // A section marker is a paragraph boundary. Deleting it outright glued the
      // last line of one section to the first line of the next whenever the model
      // wrote `[END_EXPLANATION][START_VERIFICATION]` with no newline between \u2014
      // routine for the free pool \u2014 and two unrelated paragraphs rendered as one.
      // The `\n{3,}` collapse below absorbs the blank lines this adds.
      .replace(/\[\/?(?:START|END)_[A-Z0-9_]+\]/g, '\n\n')
      .replace(/[\u200B-\u200D\uFEFF]/g, ''),
  ).split(FENCED_BLOCK).map((part, index) => (
    // Odd parts are the fences, kept as written.
    index % 2 ? part : part.replace(STRAY_HASH_LINE, '')
  )).join('').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * The line the turn opens with: the model's own reading of the request.
 *
 * It is written by the requirement-analysis call, which is already asked to
 * start by saying back what it understood in an engineer's voice, and arrives
 * on its own stream channel. A template was tried here first and read like a
 * form letter — restating a request is a judgement about what matters in it,
 * which is exactly the thing a template cannot do.
 *
 * Until it arrives, the only thing stated is which files came along: the model
 * is never told their names, so it could not mention them even if it wanted to.
 */
function buildRequestRestatement(
  message: Message,
  options: BuildTurnPresentationOptions,
): string | undefined {
  // By marker when the model obliged, by position when it did not. Small models
  // drop the markers often enough that relying on them alone leaves the turn
  // opening with nothing while the sentence sits buried in the fold. Which of
  // the two applies is settled once, in `splitTurnOpening`, together with what
  // the fold keeps — deciding it here as well is how the line came to be
  // written twice from two different sources.
  const spoken = splitTurnOpening(message).line;
  if (spoken) return spoken;

  const files = (options.userRequest?.fileNames || []).filter(Boolean);
  if (files.length === 0) return undefined;
  return options.language !== 'English'
    ? `已收到 ${files.join('、')}。`
    : `Received ${files.join(', ')}.`;
}

type BodySource = 'summary' | 'explanation' | 'content';

/**
 * Which parsed section carries this turn's main body.
 *
 * `SUMMARY` is the structured deliverable body the generation prompt asks for
 * (项目概要 / 需求概述 / 代码实现 / 关键假设 / 方案设计); `EXPLANATION` is the
 * correction analysis and only becomes the body when there is no summary.
 */
function getBodySource(message: Message): BodySource {
  if (hasText(message.parsed?.summary)) return 'summary';
  if (hasText(message.parsed?.explanation)) return 'explanation';
  return 'content';
}

/**
 * @param programDelivered whether `ProgramSection` will render the program on
 *   this turn. When it will, the body must not carry it as well: the raw model
 *   text still contains the whole `[START_CODE]` section, and printing it here
 *   too shows the same program twice.
 */
function getBodyText(message: Message, programDelivered: boolean): string {
  const source = getBodySource(message);
  if (source === 'summary') return stripInternal(message.parsed?.summary);
  if (source === 'explanation') return stripInternal(message.parsed?.explanation);
  // Raw model text: canonicalize the markers before anything reads section shape.
  // The parsed sections above arrive from `extractTaggedSection` already canonical.
  const raw = fenceFlowchartSections(normalizeStructuredMarkers(message.content));
  // A model that wrote no SUMMARY and no EXPLANATION leaves the raw response as
  // the body, program included. `stripInternal` deletes the markers but not what
  // they wrapped, so the program reached the renderer as bare prose: markdown
  // joined every line of it into one paragraph — the same defect
  // `fenceProgramSections` was written for, which only the streaming path was
  // ever given. Where the program has its own card the section is dropped; where
  // nothing else will render it, it is fenced so it is at least a code block.
  return stripInternal(programDelivered ? dropProgramSections(raw) : fenceProgramSections(raw));
}

/**
 * Validation findings, kept addressable.
 *
 * "急停联锁缺失" is an order of magnitude less useful than "急停联锁缺失 · 第 42 行":
 * an engineer reviewing a program they did not write needs to be taken to the
 * place, not told there is a place.
 */
function toTurnIssue(raw: unknown, fallbackId: string): TurnValidationIssue | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const record = raw as Record<string, unknown>;
  const messageText = compactText(record.message || record.description || record.title, 240);
  if (!messageText) return undefined;
  // Every issue the CODESYS gate contributes is coded `CODESYS_*` — the compile
  // diagnostics, the industrial-rule failures, and the adapter's own fallbacks.
  // TIA's gate (TA5) codes its own the same way, `TIA_*`, once a Siemens turn
  // hands its program to it. Nothing else in the validator uses either prefix,
  // so it is enough to tell a compiler's findings from the built-in checker's.
  const code = typeof record.code === 'string' ? record.code : '';
  return {
    id: typeof record.id === 'string' && record.id ? record.id : fallbackId,
    line: typeof record.line === 'number' && record.line > 0 ? record.line : undefined,
    severity: record.severity === 'error' || record.severity === 'warning' || record.severity === 'info'
      ? record.severity
      : 'error',
    message: messageText,
    suggestion: compactText(record.suggestion, 240) || undefined,
    ...(code
      ? { source: code.startsWith('CODESYS_') || code.startsWith('TIA_') ? 'compiler' as const : 'preflight' as const }
      : {}),
    ...(typeof record.category === 'string' && record.category ? { category: record.category } : {}),
  };
}

function buildValidationIssues(message: Message): TurnValidationIssue[] | undefined {
  const report = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  const rawIssues = Array.isArray(report?.issues) ? report.issues : [];
  const issues = rawIssues
    .map((raw, index) => toTurnIssue(raw, `issue-${index}`))
    .filter((issue): issue is TurnValidationIssue => Boolean(issue));

  return issues.length > 0 ? issues : undefined;
}

function repairOutcome(value: unknown): TurnRepairAttempt['outcome'] {
  return value === 'applied' || value === 'skipped' || value === 'failed' || value === 'discarded'
    ? value
    : undefined;
}

/** The edit size a round reported, when it reported a well-formed one. */
function readDiffStat(value: unknown): TurnRepairAttempt['diffStat'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const added = Number(record.added);
  const removed = Number(record.removed);
  if (!Number.isFinite(added) || !Number.isFinite(removed)) return undefined;
  if (added <= 0 && removed <= 0) return undefined;
  return { added: Math.max(0, added), removed: Math.max(0, removed) };
}

/**
 * The compile a round was judged by, when the worker stamped one on.
 *
 * Counts are read defensively but a missing one is not invented: a round with a
 * malformed stamp reports no compile rather than a clean one, since "0 errors"
 * is the single claim that must never be made about a program nothing compiled.
 */
function readNativeCompile(value: unknown): TurnRepairAttempt['nativeCompile'] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  const errors = Number(record.errors);
  const warnings = Number(record.warnings);
  if (!Number.isFinite(errors) || !Number.isFinite(warnings)) return undefined;
  const attemptNo = Number(record.attemptNo);
  const skippedReason = compactText(record.skippedReason, 120) || undefined;
  const skippedDetail = compactText(record.skippedDetail, 300) || undefined;
  // TA5: which native compiler produced this stamp. Optional and validated
  // against the two known values — an unrecognised or missing one is left out
  // rather than guessed, so an old stamp with no `compiler` field still parses
  // and a round with one names the compiler that actually judged it.
  const compiler = record.compiler === 'tia' || record.compiler === 'codesys' ? record.compiler : undefined;
  return {
    ...(Number.isFinite(attemptNo) && attemptNo > 0 ? { attemptNo } : {}),
    errors: Math.max(0, errors),
    warnings: Math.max(0, warnings),
    ok: record.ok === true,
    ...(compiler ? { compiler } : {}),
    ...(skippedReason ? { skippedReason } : {}),
    ...(skippedDetail ? { skippedDetail } : {}),
  };
}

/** Text lines a repair round recorded about what it changed. */
function repairNotes(record: Record<string, unknown>): string[] {
  const notes = Array.isArray(record.strategyNotes) ? record.strategyNotes : [];
  return notes
    .map(note => compactText(note, 200))
    .filter(hasText)
    .slice(0, 12);
}

/**
 * Every round of repair, as what it found and what it did about it.
 *
 * The data was already on the message and rendered nowhere: the turn stated
 * "已修复" and stopped, which asks the engineer to take the fix on faith. A round
 * is kept whenever it has anything to account for; one that recorded nothing at
 * all is left out rather than shown as an empty row.
 */
function buildRepairAttempts(message: Message): TurnPresentation['repairAttempts'] {
  const raw = message.repairAppendix?.attempts;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;

  const attempts = raw.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as unknown as Record<string, unknown>;
    const found = (Array.isArray(record.issues) ? record.issues : [])
      .map((issue, issueIndex) => toTurnIssue(issue, `repair-${index}-${issueIndex}`))
      .filter((issue): issue is TurnValidationIssue => Boolean(issue));
    const notes = repairNotes(record);
    const reason = compactText(record.reason, 240) || undefined;
    const summary = compactText(record.summary, 240) || undefined;
    const result = compactText(record.result, 240) || undefined;
    const discardReason = compactText(record.discardReason, 240) || undefined;
    const nativeCompile = readNativeCompile(record.nativeCompile);
    // Which of the loop's two budgets this round spent, as the runtime stamped it.
    const phase: TurnRepairAttempt['phase'] = record.phase === 'checker' || record.phase === 'compile'
      ? record.phase
      : undefined;

    // A compiler verdict is an account of the round on its own — a round whose
    // only news is "CODESYS still reports 3 errors" is exactly the round this
    // section exists to show, and dropping it for having no prose would hide the
    // step that proves the compiler looked again.
    if (found.length === 0 && notes.length === 0 && !reason && !summary && !result && !nativeCompile) return [];

    return [{
      attempt: typeof record.attempt === 'number' ? record.attempt : index + 1,
      ...(phase ? { phase } : {}),
      outcome: repairOutcome(record.outcome),
      reason,
      notes,
      found,
      summary,
      diffStat: readDiffStat(record.diffStat),
      result,
      discardReason,
      ...(nativeCompile ? { nativeCompile } : {}),
    }];
  });

  return attempts.length > 0 ? attempts : undefined;
}

/**
 * The account of a repair that policy would not let run.
 *
 * Built from the validation that provoked the block, so the card states what is
 * actually outstanding rather than only that something is. The root-cause
 * grouping is the same one the validation banner uses — one classification of
 * the report, not two that could disagree.
 */
function buildRepairBlocked(message: Message): TurnPresentation['repairBlocked'] {
  const context = getRepairBlockedContext(message);
  if (!context) return undefined;

  const summary = getValidationRootCauseSummary(getEffectiveValidationReport(message));
  return {
    reason: context.reason,
    signals: context.signals,
    issueCount: summary.rawIssueCount,
    rootCauses: summary.groups.map(group => ({
      key: group.key,
      title: group.title,
      count: group.count,
      lines: group.lines,
      deterministic: Boolean(group.deterministicRepairId),
      messages: group.messages,
      severity: group.severity,
    })),
  };
}

/**
 * The preflight's own findings, folded to root causes.
 *
 * Shares `getValidationRootCauseSummary` with the blocked-repair card on purpose:
 * one classification of the report, not two that could disagree about how many
 * problems the program has.
 */
function buildPreflightFindings(message: Message): TurnPresentation['preflightFindings'] {
  // The preflight's own report first. `getEffectiveValidationReport` deliberately
  // prefers the post-repair verdict — right for "did this turn end clean", wrong
  // for "what did this step find", because a repair that succeeds empties it and
  // the step is left stating a count with nothing under it.
  const sealed = message.preflightReport ?? undefined;
  const summary = getValidationRootCauseSummary(sealed ?? getEffectiveValidationReport(message));
  if (summary.groups.length === 0) return undefined;
  return {
    issueCount: summary.rawIssueCount,
    reportedIssueCount: summary.reportedIssueCount,
    rootCauses: summary.groups.map(group => ({
      key: group.key,
      title: group.title,
      count: group.count,
      lines: group.lines,
      deterministic: Boolean(group.deterministicRepairId),
      messages: group.messages,
      severity: group.severity,
    })),
    // Only the tab that watched the turn stream holds the preflight's own
    // report: the server stores none, so a reloaded turn has the latest report
    // on record and nothing else — after a repair or a compile, a later check's.
    // The row above states the preflight's count, sealed when it ran; a list
    // under it that counts another check says so (2026-09-28, run 2, reloaded).
    ...(sealed === undefined && checkedAgainSincePreflight(message) ? { fromLaterCheck: true as const } : {}),
  };
}

/** Whether anything validated the program again after the preflight: a repair, or a native compile. */
function checkedAgainSincePreflight(message: Message): boolean {
  return Boolean(
    message.stageBuffers?.repair?.started
    || message.stageBuffers?.codesysValidation?.started
    || message.repairAppendix?.attempts?.length
    || message.repairAppendix?.finalValidation,
  );
}

/**
 * The generated test cases.
 *
 * They were shown by the render path deleted in c956a04 and have been carried on
 * the message but displayed nowhere since — only counted, in the workspace
 * panel. A case with no steps and no expectations is not a test, so it is
 * dropped rather than listed as a bare title.
 */
function buildTestCases(message: Message): TurnPresentation['testCases'] {
  const raw = message.pipeline?.testCases;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;

  const lines = (value: unknown): string[] => (Array.isArray(value) ? value : [])
    .map(item => compactText(item, 200))
    .filter(hasText)
    .slice(0, 12);

  const cases = raw.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as unknown as Record<string, unknown>;
    const title = compactText(record.title, 160);
    const steps = lines(record.steps);
    const expected = lines(record.expected);
    if (!title || (steps.length === 0 && expected.length === 0)) return [];

    return [{
      id: typeof record.id === 'string' && record.id ? record.id : `case-${index}`,
      title,
      category: typeof record.category === 'string' ? record.category : 'normal',
      preconditions: lines(record.preconditions),
      steps,
      expected,
    }];
  });

  return cases.length > 0 ? cases : undefined;
}

/**
 * How many engineering decisions the user confirmed, how many a written-down
 * default closed, and how many the model assumed on their behalf.
 *
 * Read from the turn's spec closure and from nothing else. The arithmetic
 * lives in `decisionCountsFromClosure`, which the workspace spec panel reads
 * too: the line above a program and the panel describing the project it
 * belongs to must not be able to disagree about who decided what.
 *
 * A turn with no closure says nothing. The count used to fall back to the
 * model's own prose until the closure arrived: `ASSUMPTION:` lines, or the
 * items under 「关键假设」, whichever template that turn happened to use. So the
 * line above a program could change under its reader, and a list the model
 * wrote one way was counted another. No number is better than one that
 * depends on how the model phrased itself.
 */
function buildDecisionProvenance(message: Message): TurnPresentation['decisionProvenance'] {
  return decisionCountsFromClosure(message.pipeline?.specClosure);
}

/**
 * The two program versions worth comparing on this turn.
 *
 * A repair replaces a program that the engineer may already have reviewed; the
 * question that matters then is "what changed", not "here is 400 lines again".
 *
 * The repair chain only reaches versions produced inside this one turn, so when
 * it has nothing to compare the previous *generation* stands in: regenerating a
 * program raises exactly the same question, and answering it with two hundred
 * unmarked lines is the same non-answer.
 */
function getCodeComparison(
  message: Message,
  options: BuildTurnPresentationOptions,
): TurnPresentation['codeComparison'] {
  return getRepairComparison(message) || getGenerationComparison(message, options);
}

/** What this turn's repair loop did: the program it started from against the one it delivered. */
function getRepairComparison(message: Message): TurnPresentation['codeComparison'] {
  const versions = getRepairCodeVersions(message.repairAppendix, message.parsed?.code);
  const delivered = findDeliveredVersion(versions);
  const base = versions.find(version => version.role === 'base') || versions[0];
  // The version the engineer was left with, against the one this turn started
  // from — never the tail of the chain.
  //
  // The loop records every candidate it tries, and the ones it rejected sit at
  // the end. Comparing the last two entries showed a change that was discarded:
  // real repair work, correctly refused, rendered as though it were the
  // delivery, directly above a summary saying the base was left unchanged.
  //
  // When nothing was adopted, `delivered` is the base and there is no in-turn
  // change to show. That falls through to the previous generation below, which
  // is the comparison that still carries information.
  if (delivered && base && delivered !== base) {
    if (hasText(base.code) && hasText(delivered.code) && base.code.trim() !== delivered.code.trim()) {
      return {
        beforeLabel: base.label || base.id,
        afterLabel: delivered.label || delivered.id,
        before: base.code,
        after: delivered.code,
      };
    }
  }
  return undefined;
}

function getGenerationComparison(
  message: Message,
  options: BuildTurnPresentationOptions,
): TurnPresentation['codeComparison'] {
  const delivered = findDeliveredVersion(getRepairCodeVersions(message.repairAppendix, message.parsed?.code));
  const previous = options.previousGeneration;
  // The program as it was written, not as it is displayed: the repair branch
  // above compares raw stored versions, and mixing a re-indented side into the
  // other comparison would report the display pipeline as though it were an edit.
  const current = delivered?.code
    || message.parsed?.code
    || '';
  if (!previous || !hasText(previous.code) || !hasText(current)) return undefined;
  if (previous.code.trim() === current.trim()) return undefined;

  return {
    beforeLabel: previous.versionLabel,
    afterLabel: options.codeVersionLabel || 'V2.0',
    before: previous.code,
    after: current,
  };
}

/**
 * What this delivery is, stated before the program rather than after it.
 *
 * Only written when the turn is not the first delivery: on a first generation
 * there is no lineage to state, and "本次交付 V1.0" is noise. The line size is
 * measured against the version being replaced so the sentence carries a fact,
 * not just a number.
 */
function buildVersionNote(
  comparison: TurnPresentation['codeComparison'],
  options: BuildTurnPresentationOptions,
): string | undefined {
  const version = options.codeVersionLabel?.trim();
  const previous = options.previousGeneration?.versionLabel?.trim();
  if (!previous || !version || version === previous) return undefined;

  const isChinese = options.language !== 'English';
  const change = comparison && comparison.beforeLabel === previous
    ? measureCodeChange(comparison.before, comparison.after)
    : undefined;
  const delta = change && (change.added > 0 || change.removed > 0)
    ? isChinese
      ? `：新增 ${change.added} 行，删除 ${change.removed} 行`
      : `: +${change.added} / −${change.removed} lines`
    : '';
  // A version on the last one's major digit is a change made in that program;
  // only a program written again takes the next major (resolveRepairVersionPlan).
  const majorOf = (label: string) => /^V(\d+)\./i.exec(label)?.[1];
  const changedInPlace = majorOf(version) !== undefined && majorOf(version) === majorOf(previous);

  return isChinese
    ? `本次交付 ${version}，在 ${previous} 基础上${changedInPlace ? '修改' : '重新生成'}${delta}。`
    : `Delivering ${version}, ${changedInPlace ? 'changed' : 'regenerated'} from ${previous}${delta}.`;
}

/**
 * Real token spend for this turn, summed over every model attempt.
 *
 * Only reported when the provider returned actual counts — an estimate shown as
 * a hard number would be worse than showing nothing, especially under autofree
 * routing where the user is watching cost.
 */
function getTokenUsage(message: Message): TurnPresentation['tokenUsage'] {
  const attempts = message.generationMetrics?.modelAttempts || [];
  let promptTokens = 0;
  let completionTokens = 0;
  let totalTokens = 0;
  for (const attempt of attempts) {
    promptTokens += attempt.actualPromptTokens || 0;
    completionTokens += attempt.actualCompletionTokens || 0;
    totalTokens += attempt.actualTotalTokens
      || (attempt.actualPromptTokens || 0) + (attempt.actualCompletionTokens || 0);
  }
  if (totalTokens <= 0) return undefined;
  return { promptTokens, completionTokens, totalTokens };
}

function getWorkTrace(message: Message): TurnPresentation['workTrace'] {
  const metrics = message.generationMetrics;
  if (!metrics) return undefined;
  const contract = metrics.jianContextContract;
  const trace = metrics.jianToolTrace || [];
  const profile = metrics.vendorExecutionProfile;
  const routing = metrics.routingDecision;
  if (!contract && trace.length === 0 && !profile && !routing) return undefined;

  return {
    route: routing?.turnRoute,
    scope: routing?.workScope,
    reason: routing?.reason,
    contextItems: contract?.assembled.length || 0,
    tools: trace.map(item => ({ name: item.tool, status: item.status })),
    targetBrand: profile?.targetBrand,
    supportLevel: profile?.supportLevel,
    effectiveRuleBrand: profile?.ruleProvenance.effectiveRuleBrand,
  };
}

const CODE_SECTION_RE = /\[START_CODE\]([\s\S]*?)(?=\[END_CODE\]|\[\/?START_[A-Z0-9_]+\]|$)/gi;

/**
 * The program, wrapped in the fence its markers never were.
 *
 * `[START_CODE]` is not a code fence, so once the marker was stripped the
 * program arrived at the renderer as bare prose: markdown joined all of it into
 * a single paragraph and HTML collapsed the indentation, so a generation that
 * was streaming perfectly well still read as one garbled run-on line. Fencing it
 * hands the block to `CodeBlock`, which routes ST to `CodeLineRows` for
 * indentation and highlighting — the same shape the finished program takes in
 * `ProgramSection`, so completion settles the turn instead of reflowing it.
 *
 * The span ends at the next section marker as well as at its own closing one,
 * so a program still arriving is fenced from the first line rather than after
 * the last.
 */
function fenceProgramSections(raw: string): string {
  return raw.replace(CODE_SECTION_RE, (_full, section: string) => {
    const inner = String(section).replace(/^\r?\n/, '').trimEnd();
    if (!inner.trim()) return '';
    // The generation prompt's own template puts a ```st fence inside the
    // section. Wrapping that a second time prints the model's fence verbatim.
    if (inner.trimStart().startsWith('```')) return `\n\n${inner.trim()}\n\n`;
    return `\n\n\`\`\`st\n${inner}\n\`\`\`\n\n`;
  });
}

/** The program removed from the body, for turns where its own card renders it. */
function dropProgramSections(raw: string): string {
  return raw.replace(CODE_SECTION_RE, '\n\n');
}

/** A `[START_FLOWCHART]` section that has finished arriving. */
const FLOWCHART_SECTION_RE = /\[START_FLOWCHART\]([\s\S]*?)(?=\[\/?END_FLOWCHART\]|\[\/?START_[A-Z0-9_]+\])/gi;
/** A `[START_FLOWCHART]` with nothing after it yet: the diagram is still being written. */
const OPEN_FLOWCHART_SECTION_RE = /\[START_FLOWCHART\][\s\S]*$/i;

/**
 * The diagram, wrapped in the fence the prompt tells the model not to write.
 *
 * `buildFlowchartInstruction` asks for bare mermaid inside `[START_FLOWCHART]`
 * — "不要将图表包裹在 markdown 代码围栏中" — so once the markers were stripped the
 * chart reached the renderer as prose and markdown joined `stateDiagram-v2`,
 * every state and every transition into one run-on paragraph. Same defect
 * `fenceProgramSections` fixes for `[START_CODE]`, and the same remedy: fencing
 * it hands the block to `Markdown`, which routes `mermaid` to `MermaidDiagram`.
 *
 * A section still arriving is dropped rather than fenced. Half a diagram cannot
 * be parsed, and `hideIncompleteStreamingMermaid` already holds back the model's
 * own unclosed fences for that reason — this keeps an unfenced one to the same
 * rule instead of flashing a syntax error until the last state lands.
 */
function fenceFlowchartSections(raw: string): string {
  return raw
    .replace(FLOWCHART_SECTION_RE, (_full, section: string) => {
      const inner = String(section).trim();
      if (!inner) return '';
      if (inner.startsWith('```')) return `\n\n${inner}\n\n`;
      return `\n\n\`\`\`mermaid\n${inner}\n\`\`\`\n\n`;
    })
    .replace(OPEN_FLOWCHART_SECTION_RE, '');
}

/**
 * Where the delivery summary starts in a turn that is still being written.
 *
 * It is written after the repair loop and the compiler — `writeDeliveryNarration`
 * is the only call that writes [START_SUMMARY] now — and it streams into the same
 * content as the program. Drawn with the rest of the body inside the code step,
 * it spilled out between the program and the preflight, the repair rounds and
 * the compile (2026-09-22). Everything from its opening marker on goes after the
 * steps, where the settled summary appears; a marker that has only half arrived
 * is held back, so its first characters never flash in the code step.
 */
const LIVE_NARRATION_OPEN = '[START_SUMMARY]';

function splitLiveNarration(raw: string): { body: string; narration: string } {
  const at = raw.indexOf(LIVE_NARRATION_OPEN);
  if (at >= 0) return { body: raw.slice(0, at), narration: raw.slice(at) };
  for (let length = LIVE_NARRATION_OPEN.length - 1; length > 0; length -= 1) {
    if (raw.endsWith(LIVE_NARRATION_OPEN.slice(0, length))) {
      return { body: raw.slice(0, raw.length - length), narration: '' };
    }
  }
  return { body: raw, narration: '' };
}

function liveContent(message: Message): { body: string; narration: string } {
  // Canonicalize first. Every rule below matches on `[START_X]`, while the free
  // pool routinely writes the marker bare — `START_THOUGHT` on its own line, or
  // just `FLOWCHART:`. `stripStructuredTags` puts the brackets back, but it runs
  // last, so a bare marker used to reach it having already skipped the reasoning
  // cut and both fences: the reasoning printed twice and the program and the
  // diagram arrived as prose. Normalizing here is what makes those rules see the
  // section at all.
  // Not `planGeneration.text`. Everything that stage streams is already on
  // screen twice over: folded under 方案规划 as the reasoning it is, and — in
  // the structured form the user actually reads — in the plan card. Printing it
  // here as well put the whole plan (requirement table, subtasks, risks, the
  // JSON heading the model was asked not to write) under the card as a third
  // copy, and left it standing there after the plan had been approved.
  return splitLiveNarration(normalizeStructuredMarkers(
    message.content
    || message.stageBuffers?.codeGeneration?.text
    || '',
  ));
}

/** See `LIVE_NARRATION_OPEN`. */
function getLiveNarration(message: Message): string {
  const { narration } = liveContent(message);
  return narration ? renderLiveText(narration) : '';
}

/**
 * Text to render live while the turn is still being written.
 *
 * The user must see prose growing within the first seconds, not just a spinner,
 * so the raw stream is shown as it arrives. A half-written mermaid fence is held
 * back until it closes — a partial diagram cannot be parsed.
 */
function getStreamingBody(message: Message): string {
  return renderLiveText(liveContent(message).body);
}

function renderLiveText(raw: string): string {
  // The reasoning section belongs to the step that produced it and is already
  // on screen above, folded. Leaving it here as well prints it twice — the
  // runtime sends every chunk to both the stage buffer and the body.
  //
  // The span ends at the next section marker as well as at its own closing one,
  // matching `extractTaggedSection`. Without that boundary an unclosed
  // `[START_THOUGHT]` ran to end-of-string and took the program with it, so a
  // model that skipped `[END_THOUGHT]` — routine for the free pool — produced a
  // turn whose body stayed empty to the end: a generation that read as a hang.
  // Lookahead, so the marker that ends the span is left for the next rule.
  const withoutReasoning = raw
    .replace(/\[START_THOUGHT\][\s\S]*?(?=\[END_THOUGHT\]|\[\/?START_[A-Z0-9_]+\]|$)/gi, '')
    .replace(/\[\/?END_THOUGHT\]/gi, '');
  // Mermaid is settled first, on the untouched text: its "is this fence still
  // open" test accepts any bare ``` line as the close, and a program fence added
  // ahead of it would answer that question for it.
  const settled = hideIncompleteStreamingMermaid(withoutReasoning, true);
  return stripInternal(fenceProgramSections(fenceFlowchartSections(settled)));
}

/**
 * RC5 (clarify card root-cause 2026-07-23): the backend clarify hint
 * ("> 请通过下方选项确认以上信息…") is instruction boilerplate, not a
 * requirement understanding. It must never be glued after "我理解你需要：".
 */
function isClarifyBoilerplate(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (trimmed.startsWith('>')) return true;
  return /请通过下方选项|确认以上信息|补充后我将进入|please confirm the options below/i.test(trimmed);
}

function getRequirementUnderstanding(message: Message): string {
  const requirement = message.pipeline?.requirement;
  const candidates = [
    requirement?.normalizedPrompt,
    requirement?.rawPrompt,
    requirement?.desiredOutputs?.[0],
    message.parsed?.clarify,
    message.content,
  ];
  const source = candidates.find(
    (value): value is string => typeof value === 'string' && !isClarifyBoilerplate(value),
  ) || '';
  return compactText(source, 360);
}

function getFailureText(message: Message, options: BuildTurnPresentationOptions): string {
  const report = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  const repair = message.repairAppendix;
  // The raw stream is the last resort here, and `compactText` — unlike
  // `stripInternal` — does not remove structured markers. Reaching that fallback
  // while the turn was still being written printed `[START_THOUGHT]`,
  // `[START_CODE]` and the rest verbatim into the recovery banner, which renders
  // as preformatted plain text with no filter of its own.
  const reportSummary = typeof report?.summary === 'string' ? report.summary : undefined;
  // Not the repair summary when the repair step is on screen.
  //
  // `turnSteps` draws that exact field as the step's own conclusion, in body
  // type, directly under 「修复问题」. Repeating it here printed it a second time
  // inside the amber banner several rows lower — the same sentence twice, once
  // where it was reached and once where it reads as the turn's headline.
  //
  // Only when the step is actually there: a turn whose repair stage buffer never
  // arrived has no other place to say what the repair concluded.
  const repairSummary = message.stageBuffers?.repair ? undefined : repair?.summary;
  // 进行时的句子不能当作已结束回合的标题。
  //
  // 下面三级——两个 stage buffer 的 message 和 progressState.label——写的都是"正在
  // 做什么"，是给还在跑的回合用的。回合结束后它们并不会被清掉，于是一个
  // status=completed、progress=100、结论横幅和交付物都已经出齐的回合，头顶上挂着
  // 一句「正在根据校验结果修复代码…」。读者会以为还要再等，实际上什么都不会再发生。
  //
  // 回合settled 之后只用真正描述结局的来源；没有的话宁可空着，让 RecoveryTurn 回退
  // 到它自己的静态措辞，也好过播一句永远不会兑现的进度。
  // 例外：失败的回合，这几级装的就是失败原因本身。
  //
  // `progressState.label` 在 `phase: 'failed'` 下不是进度而是结论——「Response stream
  // stalled」这类只存在于这里。一刀切地把它们滤掉，等于把失败回合唯一的解释也抹掉。
  const settled = !message.isStreaming;
  const failed = message.progressState?.phase === 'failed';
  // TA10b: `stageBuffers.repair.message` is the loop's last "now doing" line,
  // and `applyStageChunkToBuffers` never clears it once the stage ends — so it
  // kept reading as the headline for the ten-plus minutes the final
  // validation and the delivery notes still took after the loop itself
  // stopped. Once the stage reports done, that line is stale; say what is
  // actually true right now instead.
  const repairStageDone = isRepairStageDone(message);
  const inFlightText = settled && !failed
    ? undefined
    // `!failed`: a turn whose phase has already moved to 'failed' must keep
    // falling through to `progressState.label`, which for a failed phase is
    // the failure reason itself (see the comment above) — not the
    // final-checks line, which would claim things are still going fine.
    : repairStageDone && message.isStreaming && !failed
      // Wording (review): "已完成" overclaimed when no round was actually
      // applied — the repair stage can be *done* (its own buffer says so)
      // without anything having succeeded, or even having been needed.
      ? (options.language !== 'English'
        ? '修复已结束，正在进行最终校验…'
        : 'Repair finished; running final checks…')
      // Review fix: `failed` puts `progressState.label` first, and — when
      // there is no label at all — a generic failure line, never the stale
      // stage-start messages. The worker sets `stageBuffers.repair.message`
      // once at stage_start and never clears it, so on a failed turn it is
      // still sitting there describing the repair loop's last round, not why
      // the turn actually failed; falling back to it here would be the same
      // false claim the label-first ordering exists to stop, just reached
      // through the one gap that ordering alone leaves open.
      : failed
        ? message.progressState?.label
          || translate(options, 'Generation failed', options.language !== 'English' ? '生成失败' : 'Generation failed')
        : message.stageBuffers?.repair?.message
          || message.stageBuffers?.validation?.message
          || message.progressState?.label;
  return compactText(
    stripStructuredTags(
      repairSummary
        || reportSummary
        || inFlightText
        || message.content,
    ),
    560,
  );
}

function isPlanReviewPending(message: Message): boolean {
  const review = getWorkspaceMessagePlanReview(message);
  return Boolean(review && review.requiresApproval !== false && !review.reviewResult);
}

/**
 * The worker's record of every CODESYS compile of the turn (C1), from the
 * compile stage. Only a record with at least one compile counts: the stage opens
 * at the first dispatch, so any record in it is a dispatched compile.
 */
function readCodesysCompiles(message: Message): TurnPresentation['codesysCompiles'] {
  const buffer = message.stageBuffers?.codesysValidation;
  if (!buffer || buffer.artifactType !== 'codesys_compile_log') return undefined;
  const log = buffer.artifact as CodesysCompileLog | undefined;
  if (!log || !Array.isArray(log.compiles) || log.compiles.length === 0) return undefined;
  return {
    log,
    stageDone: buffer.done === true,
    stageCancelled: buffer.terminalReason === 'cancelled',
  };
}

/** The worker's CODESYS gate record, when the turn carries one. */
function readTurnNativeCompile(message: Message): NativeCompileRecord | undefined {
  const record = message.generationMetrics?.codesysValidation as unknown;
  return record && typeof record === 'object' && !Array.isArray(record)
    ? record as NativeCompileRecord
    : undefined;
}

function isValidationFailed(message: Message): boolean {
  const report = getWorkspaceValidationReport(message) as Record<string, unknown> | null;
  return Boolean(report && report.valid === false)
    || message.stageBuffers?.validation?.status === 'failed';
}

function buildActions(
  kind: TurnKind,
  artifacts: ArtifactFile[],
  options: BuildTurnPresentationOptions,
): TurnAction[] {
  const actions: TurnAction[] = [];
  const isChinese = options.language !== 'English';
  const hasUnvalidatedProgram = artifacts.some(
    artifact => artifact.kind === 'plc_program' && artifact.status === 'unvalidated',
  );
  if (hasUnvalidatedProgram && artifacts.some(artifact => artifact.kind === 'validation')) {
    actions.push({
      id: 'open-validation',
      kind: 'open_validation',
      label: isChinese ? '查看校验问题' : 'View validation issues',
      primary: true,
    });
  }
  // 没有「查看 PLC 程序」这一项。
  //
  // 它做的事是把工作区切到 artifacts 标签页，而那正是它下面那张「本轮生成产物」卡片
  // 已经列全的东西——程序、POU、DUT、文档、交付包，每一项都能点开预览、能下载。
  // 一个按钮和它正下方的清单指向同一个地方，读者要先分辨一次才知道两者是一回事。
  if (!hasUnvalidatedProgram && artifacts.some(artifact => artifact.kind === 'validation')) {
    actions.push({
      id: 'open-validation',
      kind: 'open_validation',
      label: isChinese ? '查看校验结果' : 'View validation results',
    });
  }
  return actions.slice(0, 3);
}

/** The first line carrying text — enough to tell whether the body already prints a section. */
function leadLine(text: string): string {
  return text.split('\n').map(line => line.trim()).find(Boolean) || '';
}

/**
 * @param bodyText what the turn prints as its body. A section the body already
 *   carries must not be listed behind a chip as well: with no SUMMARY and no
 *   EXPLANATION the body is the whole model response — fault register, risks and
 *   open questions included — so each of them stood on screen twice, once in
 *   body type and once inside the appendix.
 */
function buildDeliverable(
  message: Message,
  bodySource: BodySource,
  bodyText: string,
): TurnPresentation['deliverable'] {
  const unlessInBody = (text: string | undefined): string | undefined => {
    if (!text) return undefined;
    const lead = leadLine(text);
    return lead && bodyText.includes(lead) ? undefined : text;
  };

  const faults = unlessInBody(message.parsed?.faults?.trim());
  const openQuestions = unlessInBody(message.parsed?.openQuestions?.trim());
  const risks = unlessInBody(message.parsed?.risks?.trim());
  const verification = unlessInBody(message.parsed?.verification?.trim());
  // The correction analysis is only appendix material when SUMMARY already
  // carries the body; otherwise it *is* the body and must not appear twice.
  const analysis = bodySource === 'summary' ? message.parsed?.explanation?.trim() : undefined;
  if (!faults && !openQuestions && !risks && !verification && !analysis) return undefined;
  return {
    ...(analysis ? { analysis } : {}),
    ...(verification ? { verification } : {}),
    ...(faults ? { faults } : {}),
    ...(risks ? { risks } : {}),
    ...(openQuestions ? { openQuestions } : {}),
  };
}

/**
 * The phases in which the program itself is still being written.
 *
 * Deliberately just these two. A deliverable published during them is built
 * from a version about to be replaced — the same reason a running repair
 * withholds one. The steps that do not touch the code are left alone, so a turn
 * whose last persisted phase is `validating` or `planning` still shows the files
 * it produced.
 */
const REWRITING_PHASES = new Set(['generating', 'repairing']);

export function isProgramStillBeingWritten(message: Message): boolean {
  return REWRITING_PHASES.has(String(message.progressState?.phase || ''));
}

export function buildTurnPresentation(
  rawMessage: Message,
  options: BuildTurnPresentationOptions = {},
): TurnPresentation {
  // The server's verdict outranks the browser's parse, and it is applied once,
  // here, so that the turn kind, the artifacts, the steps and the compile step
  // cannot each decide for themselves whether there is a program. It also
  // repairs rows that were stored wrong: on 2026-09-20 production kept 1204
  // characters of reasoning prose as `parsed.code` on a turn the server had
  // ended with CODE_GENERATION_INCOMPLETE, and everything downstream of that
  // field presented the turn as a delivered program.
  const message: Message = serverDeclaredNoProgram(rawMessage) && rawMessage.parsed?.code
    ? { ...rawMessage, parsed: { ...rawMessage.parsed, code: '' } }
    : rawMessage;
  const displayStrategy = deriveDisplayMode(message);
  const auditItems = buildStageTimelineItems(
    message,
    displayStrategy,
    key => options.translate?.(key) || key,
  );
  const gates = getWorkflowStageGates(message);
  const repairStatus = getRepairDisplayStatus(message);
  // Surfaced so the component layer (which only ever sees `TurnPresentation`,
  // never the raw message) can tell "repair is over, waiting on final checks"
  // apart from "repair is still running" — see `PendingVerdictReason`.
  const repairStageDone = isRepairStageDone(message);
  const validationFailed = isValidationFailed(message);
  const code = getWorkspaceMessageCode(message);
  // Raw code check (ungated) for completion detection — workspace gates
  // may hide code from the workspace view but the message still has output.
  const hasGeneratedCode = displayStrategy.mode !== 'simple' && (
    hasText(code)
    || hasText(message.parsed?.code)
    || hasText(message.repairAppendix?.finalCode)
  );

  let kind: TurnKind;

  // Detect complete LLM failure (network error, timeout) — no response at all
  const validationReportSummary = (message.pipeline?.validationReport as Record<string, unknown> | null)?.summary;
  const hasAnyOutput = hasText(message.parsed?.code)
    || hasText(message.parsed?.summary)
    || hasText(message.parsed?.explanation)
    || hasText(message.repairAppendix?.summary)
    || hasText(message.repairAppendix?.finalCode)
    || hasText(message.stageBuffers?.validation?.message)
    || hasText(message.stageBuffers?.repair?.message)
    || (message.repairAppendix?.attempts?.length ?? 0) > 0
    || hasText(validationReportSummary as string | undefined);
  const isCompleteFailure = message.progressState?.phase === 'failed'
    && !hasAnyOutput
    && !message.isStreaming;

  if (isCompleteFailure) {
    kind = 'error';
  } else if (gates.hasPendingHumanConfirmation) {
    kind = 'clarification';
  } else if (isPlanReviewPending(message)) {
    kind = 'review';
  } else if (
    repairStatus === 'running'
      || repairStatus === 'failed'
      || validationFailed
      || message.progressState?.phase === 'failed'
  ) {
    kind = 'recovery';
  } else if (hasGeneratedCode && !message.isStreaming) {
    kind = 'completed';
  } else if (message.isStreaming || getActiveAuditItem(auditItems)?.status === 'running') {
    kind = 'working';
  } else {
    kind = 'answer';
  }

  let primaryText = '';
  if (kind === 'clarification') {
    // The turn already opened with the model's own reading of the request. A
    // second, templated "what I understand you need" underneath it says the
    // same thing again in the voice the opening line exists to replace.
    const understanding = splitTurnOpening(message).line ? '' : getRequirementUnderstanding(message);
    const clarifyLead = compactText(message.parsed?.clarify || message.content, 420);
    primaryText = understanding
      ? options.language !== 'English'
        ? `我理解你需要：${understanding}`
        : `I understand that you need: ${understanding}`
      // RC5: when no real understanding is available, never surface raw
      // boilerplate — show a clean guide sentence instead.
      : isClarifyBoilerplate(clarifyLead)
        // Silent when the analysis already handed over in the model's own
        // words. Its closing paragraph says what was understood, what is still
        // open and what the cards are for; printing our template underneath it
        // is a third voice saying the thinnest version of the same thing, and
        // three provenances in four lines is what made this read as assembled.
        ? (hasText(message.stageBuffers?.requirementAnalysis?.conclusion)
          ? ''
          : options.language !== 'English'
            ? '请确认以下关键信息，确认后我将进入工程规划。'
            : 'Please confirm the key items below; I will start engineering planning afterwards.')
        : clarifyLead;
  } else if (kind === 'review') {
    primaryText = compactText(
      getWorkspaceMessagePlanReview(message)?.requirementSummary
        || getRequirementUnderstanding(message),
      520,
    );
  } else if (kind === 'recovery') {
    primaryText = getFailureText(message, options)
      || translate(
        options,
        'Validation needs attention',
        options.language !== 'English' ? '检查发现需要处理的问题。' : 'Validation found issues that need attention.',
      );
  } else if (kind === 'error') {
    // Never assert a cause here — the real one comes from `failureReason`.
    primaryText = message.progressState?.label
      || getFailureReason(message, options.language !== 'English').hint;
  } else if (kind === 'working') {
    primaryText = compactText(message.parsed?.explanation || message.content, 520);
  } else {
    primaryText = getBodyText(message, hasGeneratedCode);
  }

  const hasSafetyRisk = Boolean(message.strictSafetyCheck && validationFailed);
  const attention = deriveAttention({
    kind,
    hasSafetyRisk,
    validationFailed,
    repairFailed: repairStatus === 'failed',
  });

  // Whether the program has stopped changing. A repair round rewrites the code
  // in place, so anything presented as a finished deliverable while one is
  // running is built from a version that is about to be replaced — and its two
  // markdown reports are rendered from the validation report the repair is at
  // that moment resolving. `needs_review` is terminal and therefore settled: a
  // repair stopped by safety policy will not change the code again.
  //
  // `isStreaming` alone is not enough to say the turn is over. It lives on the
  // local copy of the message, and any copy that did not come from this tab's
  // own stream — the three-second session refresh, a reload, a second tab —
  // carries no such flag. Measured live on 2026-09-10: at 23:28:17 a turn
  // published its whole delivery — 输送线超时保护_V5_UNVALIDATED.st, the code
  // listing, 下载未验证版本, 安全校验报告, 导出交付包 — and by 23:28:39 all of it
  // was gone again and the turn was back at 「修复问题 · 1 轮修复」, which then ran
  // for another three minutes. Nothing had finished; the copy on screen simply
  // had no way to say so.
  //
  // The turn's own progress says it plainly, so ask that too.
  const isSettled = !message.isStreaming && repairStatus !== 'running' && !isProgramStillBeingWritten(message);
  // Liveness is whether the message is still streaming, not whether the turn is
  // in the `working` state: a turn being repaired reports `recovery` while it is
  // very much still going, and reading it off the kind left the repair step
  // sitting at `pending` with a grey dot for the whole time it ran. Not
  // `settled` either — that stays false forever on a turn stopped mid-repair,
  // which would leave a spinner running on a turn the user themselves ended.
  const steps = buildTurnSteps(message, auditItems, Boolean(message.isStreaming), options.translate);
  const requestRestatement = buildRequestRestatement(message, options);
  const extractedMermaid = extractMermaidContent(message);
  // A diagram written inside the body is already rendered by `Markdown`; only a
  // diagram lifted from elsewhere (plan history, stage buffers) needs its own slot.
  // The body a recovery turn shows depends on whether it is still being written
  // — settled once, here, so the mermaid check and the rendered text cannot
  // disagree about what the body is.
  const recoveryBody = message.isStreaming
    ? getStreamingBody(message)
    : getBodyText(message, hasGeneratedCode);
  const bodyForMermaidCheck = kind === 'recovery'
    ? recoveryBody
    : kind === 'working'
      ? getStreamingBody(message)
      : primaryText;
  const mermaidContent = extractedMermaid && /```\s*mermaid/i.test(bodyForMermaidCheck)
    ? undefined
    : extractedMermaid;
  // The exported markdown always carries the diagram, even when the turn renders
  // it inline from the body instead of in its own slot.
  const artifacts = isSettled
    ? buildArtifacts(message, kind, primaryText, options, extractedMermaid)
    : [];
  const codeComparison = kind === 'completed' || kind === 'recovery'
    ? getCodeComparison(message, options)
    : undefined;
  const repairComparison = kind === 'completed' || kind === 'recovery'
    ? getRepairComparison(message)
    : undefined;
  const validationState: TurnPresentation['validationState'] = validationFailed
    ? 'failed'
    : getWorkspaceValidationReport(message)
      ? 'passed'
      : message.progressState?.stage === 'validation' || message.progressState?.phase === 'validating'
        ? 'validating'
        : 'not_run';
  return {
    kind,
    primaryText,
    secondaryText: kind === 'review'
      ? translate(
        options,
        'Review before execution',
        options.language !== 'English'
          ? '请确认方案后再继续生成程序。'
          : 'Review the plan before program generation continues.',
      )
      // Recovery keeps the model's own body here. The "not deployment-ready"
      // warning is rendered from `validationState` so the body is never traded
      // away for it.
      //
      // Only once it has settled, though. While the turn is being written, the
      // live body belongs to the code-generation step, which is where it is now
      // drawn — leaving it here as well printed it twice, and printed the second
      // copy below every step that had not run yet.
      : kind === 'recovery'
        ? (message.isStreaming ? undefined : recoveryBody || undefined)
        : undefined,
    statusText: getStatusText(message, auditItems, options),
    attention,
    actions: buildActions(kind, artifacts, options),
    artifacts,
    settled: isSettled,
    live: Boolean(message.isStreaming),
    repairStageDone,
    deliverableName: resolveDeliverableName(message, primaryText, options),
    auditItems,
    hasSafetyRisk,
    validationState,
    reviewRequired: readDeliveryReview(message.generationMetrics?.reviewRequired),
    notices: message.generationMetrics?.internalEvents?.filter(event => event.userVisible).map(event => event.message),
    steps,
    requestRestatement,
    mermaidContent,
    // Whether the turn is still being written, not whether it is in the
    // `working` state. Validation failing mid-run advances the kind to
    // `recovery` while the model is still streaming, and gating on the kind
    // unmounted the program the user was reading and left an amber banner in
    // its place. Same reason the step stream is owned by the turn rather than
    // by any one of its states.
    streamingBody: message.isStreaming ? getStreamingBody(message) : undefined,
    liveNarration: message.isStreaming ? (getLiveNarration(message) || undefined) : undefined,
    tokenUsage: getTokenUsage(message),
    workTrace: getWorkTrace(message),
    codeComparison,
    repairCodeChange: repairComparison
      ? measureCodeChange(repairComparison.before, repairComparison.after)
      : undefined,
    versionNote: kind === 'completed' || kind === 'recovery'
      ? buildVersionNote(codeComparison, options)
      : undefined,
    // Not gated on the kind: whether a compiler saw the program is a fact
    // about the turn, and the turn that most needs to state it is the one
    // that went well enough to have no repair rounds to hang it on.
    ...(readTurnNativeCompile(message) ? { nativeCompile: readTurnNativeCompile(message) } : {}),
    ...(readCodesysCompiles(message) ? { codesysCompiles: readCodesysCompiles(message) } : {}),
    // The registry saying it skipped this turn means no artifact id will ever
    // arrive, which is what the CODESYS verdict hold is waiting for.
    ...(message.generationMetrics?.artifactRegistry?.skippedReason
      ? { artifactRegistrySkipped: true }
      : {}),
    ...(hasText(message.repairAppendix?.checkerPhaseSummary)
      ? { checkerPhaseSummary: message.repairAppendix?.checkerPhaseSummary }
      : {}),
    validationIssues: kind === 'completed' || kind === 'recovery'
      ? buildValidationIssues(message)
      : undefined,
    // Not gated on the turn kind: repair rounds and test cases are the account
    // of how the turn got here, and the run is worth watching while it is still
    // going. Gating them the way the artifact fields are gated would make them
    // appear only after everything had already finished.
    repairAttempts: buildRepairAttempts(message),
    repairBlocked: buildRepairBlocked(message),
    preflightFindings: buildPreflightFindings(message),
    testCases: buildTestCases(message),
    decisionProvenance: kind === 'completed' || kind === 'recovery'
      ? buildDecisionProvenance(message)
      : undefined,
    failureReason: kind === 'error'
      ? getFailureReason(message, options.language !== 'English')
      : undefined,
    totalElapsed: options.totalElapsed || computeTotalElapsed(message),
    // Which toolchain this turn ran for, off the metrics rather than off
    // `pipeline` — a turn reloaded from the server carries no pipeline at all,
    // and the toolbar chip beside it answers a different question: what is
    // selected now.
    ...((message.generationMetrics?.targetBrand || message.generationMetrics?.targetLanguage)
      ? {
        target: {
          ...(message.generationMetrics?.targetBrand ? { brand: message.generationMetrics.targetBrand } : {}),
          ...(message.generationMetrics?.targetLanguage ? { language: message.generationMetrics.targetLanguage } : {}),
        },
      }
      : {}),
    followUps: message.parsed?.followUps?.length ? message.parsed.followUps : undefined,
    deliverable: buildDeliverable(message, getBodySource(message), primaryText),
  };
}

// ── Elapsed time computation ────────────────────────────────────────

/**
 * How long the finished turn took — the same answer the live counter gives.
 *
 * This used to rank the spans itself, and ranked them the way `turnElapsed`
 * was written to stop: `clientTotalMs` first, `modelLatencyMs` last. The first
 * is one browser tab's stopwatch, which only measures the part of the turn that
 * tab was present for; the last is one model call, which is never how long a
 * turn took. Meanwhile the counter above the turn already read
 * `resolveFinalElapsedMs`, so the two numbers on one screen came from two
 * different rankings of the same fields.
 *
 * Measured live 2026-09-15: the footer closed at 「用时 18分42秒」 directly under
 * a counter that had just read 「已用 19分13秒」 — the finished turn reporting 31
 * seconds less than the clock the user had been watching.
 *
 * One resolver now, so there is one answer.
 */
function computeTotalElapsed(message: Message): string {
  const ms = resolveFinalElapsedMs(message);
  return typeof ms === 'number' && ms > 0 ? formatElapsedMs(ms) : '';
}

function formatElapsedMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}小时${m}分${s}秒`;
  if (m > 0) return `${m}分${s}秒`;
  return `${s}秒`;
}

// ── Mermaid extraction ──────────────────────────────────────────────

/**
 * Extract mermaid flowchart content from a message.
 * Searches parsed content, code blocks, and plan process history.
 */
export function extractMermaidContent(message: Message): string | undefined {
  // `[START_FLOWCHART]`, already extracted by the structured parser. This is the
  // section the generation prompt actually asks for, and it is the only source
  // that survives into a completed turn: the body there is SUMMARY, so a search
  // for ```mermaid fences finds nothing and the diagram had no slot to appear in.
  // A stray fence the model added anyway is left for `applyBaseCorrections`,
  // which strips it before rendering.
  const flowchart = message.parsed?.flowchart?.trim();
  if (flowchart) return flowchart;

  // Check parsed content for mermaid code blocks
  const content = message.content || message.parsed?.explanation || '';
  const mermaidBlock = content.match(/```mermaid\s*([\s\S]*?)```/i);
  if (mermaidBlock?.[1]?.trim()) return mermaidBlock[1].trim();

  // Check planProcessHistory for mermaid in reasoning
  if (message.planProcessHistory?.length) {
    for (const item of message.planProcessHistory) {
      const reasoning = item.reasoning || '';
      const match = reasoning.match(/```mermaid\s*([\s\S]*?)```/i);
      if (match?.[1]?.trim()) return match[1].trim();
    }
  }

  // Check stage buffers
  const buffers = message.stageBuffers;
  if (buffers) {
    for (const key of ['planBasis', 'planGeneration', 'codeGeneration']) {
      const text = (buffers as any)[key]?.text || '';
      const match = text.match(/```mermaid\s*([\s\S]*?)```/i);
      if (match?.[1]?.trim()) return match[1].trim();
    }
  }

  return undefined;
}
